import useDevPanelStore from '../store/devPanelStore';
import usePersistentStore from '../store/persistentStore';

let callCounter = 0;

export const isApiInspectorActive = () =>
  usePersistentStore.getState().apiInspectorEnabled === true;

const createCallId = () => {
  callCounter += 1;
  return `${Date.now()}-${callCounter}`;
};

const normalizeHeaders = headers => {
  if (!headers) return null;

  if (headers instanceof Headers) {
    const normalized = {};
    headers.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }

  if (Array.isArray(headers)) {
    return headers.map(([key, value]) => [key, value]);
  }

  return {...headers};
};

const parseRequestBody = body => {
  if (body == null) return null;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }
  if (body instanceof FormData) {
    const entries = {};
    body.forEach((value, key) => {
      entries[key] = typeof value === 'object' && value?.name ? `[File: ${value.name}]` : value;
    });
    return entries;
  }
  return body;
};

const summarizeResponse = (data, responseType) => {
  if (responseType === 'blob') return '[Blob]';
  if (data == null) return null;
  if (typeof data === 'object') {
    try {
      const serialized = JSON.stringify(data);
      if (serialized.length > 20000) {
        return {
          _truncated: true,
          preview: `${serialized.slice(0, 20000)}…`,
        };
      }
    } catch {
      return String(data);
    }
  }
  return data;
};

/**
 * Some base URLs are relative in development, so resolve them. Exports have to
 * carry an absolute URL to be runnable outside the browser. The path is kept
 * separately so the inspector never surfaces our internal endpoint keys.
 */
const parseUrl = url => {
  try {
    const parsed = new URL(url, window.location.origin);
    return {absolute: parsed.href, path: `${parsed.pathname}${parsed.search || ''}`};
  } catch {
    return {absolute: url, path: url};
  }
};

export const logApiCallStart = ({url, options = {}, params}) => {
  if (!isApiInspectorActive()) return null;

  const id = createCallId();
  const method = (options.method || 'GET').toUpperCase();
  const parsedUrl = parseUrl(url);

  useDevPanelStore.getState().addCall({
    id,
    url: parsedUrl.absolute,
    path: parsedUrl.path,
    method,
    params: params ?? null,
    requestHeaders: normalizeHeaders(options.headers),
    requestBody: parseRequestBody(options.body),
    status: null,
    ok: null,
    durationMs: null,
    response: null,
    error: null,
    pending: true,
    timestamp: new Date().toISOString(),
  });

  return {id, startedAt: Date.now()};
};

export const logApiCallComplete = (tracker, {status, ok, response, responseType, error}) => {
  if (!tracker?.id) return;

  useDevPanelStore.getState().updateCall(tracker.id, {
    status,
    ok,
    durationMs: Date.now() - tracker.startedAt,
    response: error ? null : summarizeResponse(response, responseType),
    error: error ?? null,
    pending: false,
  });
};
